import classNames from 'classnames';
import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { TriggerType } from 'react-popper-tooltip';
import { usePopperTooltip } from 'react-popper-tooltip';
import { useKey } from 'react-use';
import { MaterialIcon } from 'shared/ui/MaterialIcon';
import type { DataAttributes } from 'shared/lib/data-auto-test';
import { useClickOutside } from 'shared/lib/hooks';

import style from './Dropdown.module.scss';
import type { Option } from './interfaces';

export interface RenderButtonProps<T> {
  isOpen: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  selected?: T | T[];
  name?: string;
}

export interface RenderMenuProps<T> {
  isOpen: boolean;
  selected?: T | T[];
  handleClose: () => void;
  menuDefaultWidth?: number;
}

export type DropdownProps<T> = {
  renderMenu: (props: RenderMenuProps<T>) => React.ReactNode;
  name?: string;
  dataTestId?: string;
  portalDataTestId?: string;
  trigger?: TriggerType;
  rootElement?: Element;
  renderButton?: (props: RenderButtonProps<T>) => React.ReactNode;
  wrapperClassName?: string;
  selected?: T | T[];
  disabled?: boolean;
  attributes?: DataAttributes['attributes'];
};

export function DropdownBase<T extends Option>({
  dataTestId,
  portalDataTestId,
  selected,
  trigger = 'click',
  rootElement = document.querySelector('#popper-Tooltip') ?? document.body,
  renderButton,
  renderMenu,
  name,
  wrapperClassName = 'w-fit',
  disabled,
}: DropdownProps<T>) {
  const [visible, setVisible] = useState(false);

  const { getTooltipProps, setTooltipRef, setTriggerRef, tooltipRef, triggerRef } = usePopperTooltip(
    {
      followCursor: false,
      placement: 'bottom-end',
      closeOnOutsideClick: true,
      trigger,
      visible,
    },
    {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
      ],
    }
  );

  const handleClose = useCallback(() => setVisible(false), []);
  const handleTriggerBtnClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setVisible((prev) => !prev);
    },
    [visible]
  );

  useClickOutside([tooltipRef, triggerRef], handleClose);
  useKey('Escape', handleClose);

  return (
    <>
      <div
        ref={setTriggerRef}
        data-testid={dataTestId}
        className={wrapperClassName}
      >
        {renderButton ? (
          renderButton({ selected, isOpen: visible, name, onClick: handleTriggerBtnClick })
        ) : (
          <button
            name={name}
            disabled={disabled}
            type="button"
            className={classNames('hover:text-deep-blue-500', { 'text-deep-blue-500': visible, [style.open]: visible })}
            onClick={handleTriggerBtnClick}
          >
            <MaterialIcon>more_vert</MaterialIcon>
          </button>
        )}
      </div>
      {createPortal(
        <div
          data-testid={portalDataTestId ?? 'TestId__tooltip'}
          ref={setTooltipRef}
          {...getTooltipProps({
            className: classNames(style.popper, { [style.isVisible]: visible }),
            style: {
              width: triggerRef?.offsetWidth,
            },
          })}
        >
          {renderMenu({ isOpen: visible, handleClose, menuDefaultWidth: triggerRef?.offsetWidth })}
        </div>,
        rootElement
      )}
    </>
  );
}
